/**
 * Product Hero block entry.
 *
 * Importing the SCSS here makes Vite bundle the block's CSS alongside its JS.
 *
 * The page sells without this file: the swatches are radio buttons inside a
 * form that WooCommerce's own handler answers, so a visitor with the script
 * blocked can still choose a colourway and buy it. What this adds is the part
 * that cannot be done in markup — swapping the photographs and the price the
 * moment the choice changes, rather than after a round trip.
 */

import './index.scss';

/**
 * Read how this shop writes money, printed beside the variation table.
 *
 * A difference between two prices exists only here, in the browser, so there is
 * nowhere to ask the server what it should look like. The rules come across
 * instead — and if they did not, no figure is written at all rather than one
 * written in the wrong currency.
 *
 * @param {HTMLElement} root The `.product-hero` element.
 * @returns {Object|null} Symbol, separators, decimals and format, or null.
 */
function readMoney(root) {
    const source = root.querySelector('[data-money]');

    if (!source) {
        return null;
    }

    try {
        return JSON.parse(source.textContent || 'null');
    } catch {
        return null;
    }
}

/**
 * One amount, written the way this shop writes them.
 *
 * @param {number} amount The figure.
 * @param {Object} money  What readMoney() returned.
 * @returns {string} The formatted amount, empty when the rules are missing.
 */
function writeMoney(amount, money) {
    if (!money) {
        return '';
    }

    const decimals = Number(money.decimals ?? 2);
    const [whole, part] = Math.abs(amount).toFixed(decimals).split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, money.thousand ?? ',');
    const figure = part ? grouped + (money.decimal ?? '.') + part : grouped;

    /* WooCommerce's own placeholders, so a shop that puts the symbol after the
     * figure — or with a space — gets what it asked for. */
    return String(money.format ?? '%1$s%2$s')
        .replace('%1$s', money.symbol ?? '')
        .replace('%2$s', figure);
}

/**
 * Read the variation table a product printed alongside its markup.
 *
 * @param {HTMLElement} root The `.product-hero` element.
 * @returns {Array<Object>} One entry per purchasable variation.
 */
/**
 * A colour's own label, for the sentence a dimmed tile carries.
 *
 * @param {string} slug Colour term slug.
 * @returns {string} The colour's name, or the slug when it has no tile.
 */
function chosenName(slug) {
    return (
        plainName(
            document
                .querySelector(`[data-swatch="pa_color"][value="${CSS.escape(slug)}"]`)
                ?.closest('.product-hero__swatch')
        ) || slug
    );
}

/**
 * A tile's own name, without the weave printed under it.
 *
 * The caption holds both — "Toro" over "Chenille" — and every sentence that
 * quotes a fabric wants only the first. Reading `textContent` off the wrapper
 * returns them run together as "Toro Chenille", which is not a fabric anybody
 * can look up.
 *
 * @param {?Element} tile The `.product-hero__swatch` wrapper.
 * @returns {string} The name, empty when there is none.
 */
function plainName(tile) {
    const label = tile?.querySelector('.product-hero__swatch-name');

    if (!label) {
        return '';
    }

    const kind = label.querySelector('.product-hero__swatch-type');
    const text = kind ? label.textContent.replace(kind.textContent, '') : label.textContent;

    return text.replace(/\s+/g, ' ').trim();
}

function readVariations(root) {
    const source = root.querySelector('[data-variations]');

    if (!source) {
        return [];
    }

    /*
     * Addresses arrive without the uploads base, which the page sends once
     * beside them: on a seventeen-colourway sofa the table carries some six
     * hundred of them and every one began with the same sixty characters, a
     * srcset repeating that five times over. They are put back together here,
     * so nothing downstream knows the difference.
     */
    const base = source.dataset.mediaBase ?? '';

    /**
     * Put the uploads base back in front of one address, or of every address
     * in a srcset.
     *
     * @param {string} value A path, or a srcset of them.
     * @returns {string} The same, absolute.
     */
    const expand = (value) => {
        if (!base || 'string' !== typeof value || !value) {
            return value;
        }

        /* A srcset is a comma-separated list of "path width"; each path is
         * relative and each width must survive untouched. */
        return value.includes(',')
            ? value
                  .split(',')
                  .map((part) => {
                      const [path, ...rest] = part.trim().split(/\s+/);

                      return [base + path, ...rest].join(' ');
                  })
                  .join(', ')
            : base + value;
    };

    try {
        const table = JSON.parse(source.textContent) ?? [];

        for (const one of table) {
            for (const photo of [...(one.gallery ?? []), one.closeUp, one.packshot]) {
                if (!photo) {
                    continue;
                }

                for (const key of Object.keys(photo)) {
                    photo[key] = expand(photo[key]);
                }
            }
        }

        return table;
    } catch {
        /* Malformed data is not worth breaking the page over: without it the
         * form still posts and WooCommerce still resolves the variation. */
        return [];
    }
}

/**
 * Wire one product's buy column.
 *
 * @param {HTMLElement} root The `.product-hero` element.
 * @returns {void}
 */
function enhance(root) {
    const variations = readVariations(root);
    const money = readMoney(root);

    /* The colourway on show. Held because the hover preview needs to know which
     * weave it is standing in, and that is not readable from the markup. */
    let current = null;
    const form = root.querySelector('.product-hero__form');

    if (!form) {
        return;
    }

    const quantity = form.querySelector('.product-hero__quantity-input');

    for (const step of form.querySelectorAll('[data-step]')) {
        step.addEventListener('click', () => {
            const by = Number(step.dataset.step ?? 0);
            const next = Math.max(1, Number(quantity.value || 1) + by);

            quantity.value = String(next);
        });
    }

    if (!variations.length) {
        return;
    }

    const gallery = root.querySelector('.product-gallery');
    const idField = form.querySelector('[data-variation-id]');
    const prices = root.querySelectorAll('[data-price]');
    const wasPrices = root.querySelectorAll('[data-price-was]');

    /**
     * Point the gallery at a colourway's own photographs.
     *
     * The rail is rebuilt rather than re-rendered from the server: the block
     * printed the addresses of every colourway's photographs alongside its
     * markup, so this is a DOM edit and not a round trip. Nothing is fetched
     * until the browser meets the new `src`, which is why the other ten
     * colourways cost nothing until somebody asks for one.
     *
     * @param {HTMLElement} node   The `.product-gallery` element.
     * @param {Array}       images `{ thumb, full, fullSrcset }` per photograph.
     * @param {number}      opens  Which photograph to put on the stage.
     * @returns {void}
     */
    const swapGallery = (node, images, opens = 0) => {
        if (!images.length) {
            return;
        }

        /* The sets are NOT the same size across colourways: the opening one
         * carries the styled scene and a chosen colourway does not, so the rail
         * has to grow and shrink. Slots are cloned from the ones the server
         * printed rather than built from scratch here — the block owns its
         * markup, and cloning keeps whatever classes and attributes it chose. */
        fit(node.querySelector('.product-gallery__rail'), '.product-gallery__slot', images.length);
        fit(
            node.querySelector('.product-gallery__stage'),
            '.product-gallery__image',
            images.length
        );

        const rail = node.querySelectorAll('.product-gallery__thumb-image');
        const stage = node.querySelectorAll('.product-gallery__image');

        images.forEach((image, at) => {
            if (rail[at]) {
                rail[at].src = image.thumb;
                rail[at].removeAttribute('srcset');
                rail[at].closest('.product-gallery__thumb')?.setAttribute('data-thumb', String(at));
            }

            if (!stage[at]) {
                return;
            }

            stage[at].src = image.full;
            stage[at].dataset.slide = String(at);

            if (image.fullSrcset) {
                stage[at].srcset = image.fullSrcset;
            } else {
                stage[at].removeAttribute('srcset');
            }
        });

        node.dispatchEvent(
            new CustomEvent('product-gallery:show', {
                detail: { index: Math.max(0, Math.min(images.length - 1, opens)) },
            })
        );
    };

    /**
     * Make a container hold exactly `count` of something, by cloning or trimming.
     *
     * @param {HTMLElement|null} container Element holding the repeated children.
     * @param {string}           selector  What one of them looks like.
     * @param {number}           count     How many there should be.
     * @returns {void}
     */
    const fit = (container, selector, count) => {
        if (!container) {
            return;
        }

        let items = [...container.querySelectorAll(selector)];

        if (!items.length) {
            return;
        }

        /* Grown from the first, not the last: the last may be hidden, and a
         * clone would inherit that and never be seen. */
        while (items.length < count) {
            const clone = items[0].cloneNode(true);

            clone.removeAttribute('hidden');
            container.append(clone);
            items = [...container.querySelectorAll(selector)];
        }

        while (items.length > count) {
            items.pop()?.remove();
        }
    };

    /**
     * Show one variation: its photographs, its price, its labels, its id.
     *
     * @param {Object} variation One entry from the printed table.
     * @returns {void}
     */
    const show = (variation) => {
        /* Nothing has been asked for yet: the page is still arriving. `current`
         * is set at the end of the file the moment the opening variation is
         * known, so this is only ever true if that never happened. */
        const firstPaint = null === current;

        current = variation ?? current;

        if (!variation) {
            return;
        }

        if (idField) {
            idField.value = String(variation.id);
        }

        /* The hidden attribute fields are what WooCommerce resolves the
         * variation from when the form posts, so they move with the choice. */
        for (const [key, value] of Object.entries(variation.attributes ?? {})) {
            const field = form.querySelector(`[data-variation-attribute="${key}"]`);

            if (field) {
                field.value = value;
            }
        }

        for (const price of prices) {
            price.textContent = variation.price ?? price.textContent;
        }

        /* The struck-through figure shows only while this variation is on sale. */
        for (const was of wasPrices) {
            const figure = was.querySelector('[data-price-was-figure]');

            if (figure) {
                figure.textContent = variation.was ?? '';
            }

            was.hidden = !variation.was;
        }

        /* Both chooser rows are two views of one choice, so picking a colour
         * moves the fabric row with it and the other way round. */
        for (const [key, value] of Object.entries(variation.attributes ?? {})) {
            const taxonomy = key.replace(/^attribute_/, '');
            const input = form.querySelector(
                `[data-swatch="${taxonomy}"][value="${CSS.escape(value)}"]`
            );

            if (input) {
                input.checked = true;
            }

            const label = form.querySelector(`[data-choice-value="${taxonomy}"]`);

            if (label && input) {
                label.textContent =
                    input
                        .closest('.product-hero__swatch')
                        ?.querySelector('.product-hero__swatch-name')?.textContent ??
                    label.textContent;
            }
        }

        /* The gallery owns its own markup; it is told what to show rather than
         * having its DOM rewritten from here. */
        if (gallery && Array.isArray(variation.gallery)) {
            /*
             * A colour that was asked for opens on the cut-out, not on the room.
             * The scene is one photograph shared by every colourway of a piece,
             * so answering a click on Terracotta with it changes nothing on the
             * screen and reads as a control that did not work. The cut-out is
             * the only picture in the set that shows the fabric chosen.
             *
             * The page's own first paint keeps the room: nobody has asked for a
             * colour yet, and the room is what says what the piece is for.
             */
            const wanted = variation.packshot?.full ?? '';
            const at = firstPaint
                ? 0
                : Math.max(
                      0,
                      variation.gallery.findIndex((image) => image.full === wanted)
                  );

            swapGallery(gallery, variation.gallery, at);
        }

        showFabric(variation.closeUp);
        showStock(variation.inStock);
        render(variation);

        /* Tell the rest of the page which variation this now is. The
         * specification panels use it to show the chosen size's measurements
         * rather than every size's; they share no file with this block, so an
         * event is the whole interface between them. */
        document.dispatchEvent(
            new CustomEvent('product:variation', {
                detail: {
                    id: variation.id,
                    attributes: Object.fromEntries(
                        Object.entries(variation.attributes ?? {}).map(([key, value]) => [
                            key.replace(/^attribute_/, ''),
                            value,
                        ])
                    ),

                    /* The cloth in this exact colourway, and what to call it.
                     * The specification panels show both, and they change with
                     * the colour as well as with the weave — so they travel on
                     * the one event rather than the panels growing a second
                     * path of their own to work it out. */
                    closeUp: variation.closeUp ?? null,
                    sku: variation.sku ?? '',
                    fabric: (() => {
                        const tile = form
                            .querySelector('[data-swatch="pa_fabric"]:checked')
                            ?.closest('.product-hero__swatch');
                        const kind = tile
                            ?.querySelector('.product-hero__swatch-type')
                            ?.textContent?.trim();
                        const name = plainName(tile);

                        return name && kind ? `${name} (${kind})` : name;
                    })(),
                    colour:
                        form
                            .querySelector('[data-swatch="pa_color"]:checked')
                            ?.closest('.product-hero__swatch')
                            ?.querySelector('.product-hero__swatch-name')
                            ?.textContent?.trim() ?? '',
                },
            })
        );
    };

    /* Which colours sit near which, for rule 2 of §3.2. */
    const groups = (() => {
        const tag = root.querySelector('[data-colour-groups]');

        try {
            return tag ? JSON.parse(tag.textContent ?? '{}') : {};
        } catch {
            return {};
        }
    })();

    /**
     * Redraw the two dependent rows for the piece now chosen.
     *
     * Three things move together. The fabric tiles reshoot themselves in the
     * chosen colour, because a row of tiles is a comparison and comparing
     * bouclé-in-beige with velvet-in-black compares nothing. The colour row
     * shows only what the chosen fabric is actually woven in. And the line above
     * the button restates both in words.
     *
     * Nothing is hidden or greyed in the fabric row: every fabric stays
     * clickable, per §3.1. Only its picture changes.
     *
     * @param {Object} variation The variation now chosen.
     * @returns {void}
     */
    function render(variation) {
        const chosen = Object.fromEntries(
            Object.entries(variation.attributes ?? {}).map(([key, value]) => [
                key.replace(/^attribute_/, ''),
                value,
            ])
        );

        const colour = chosen.pa_color ?? '';
        const fabric = chosen.pa_fabric ?? '';

        /* Fabric tiles: this fabric in the chosen colour, else the nearest
         * shade we have, marked as approximate. */
        for (const tile of form.querySelectorAll('[data-swatch="pa_fabric"]')) {
            const chip = tile
                .closest('.product-hero__swatch')
                ?.querySelector('.product-hero__chip');
            const image = chip?.querySelector('img');

            if (!chip || !image) {
                continue;
            }

            const exact = variations.find(
                (one) =>
                    one.attributes?.attribute_pa_fabric === tile.value &&
                    one.attributes?.attribute_pa_color === colour
            );

            const near =
                exact ??
                variations.find(
                    (one) =>
                        one.attributes?.attribute_pa_fabric === tile.value &&
                        groups[one.attributes?.attribute_pa_color] === groups[colour]
                );

            const source = near?.closeUp?.swatch || near?.closeUp?.thumb || '';

            if (source && image.src !== source) {
                image.src = source;
                image.removeAttribute('srcset');
            }

            chip.toggleAttribute('data-approx', !exact);
        }

        /*
         * The colour row in two groups: what this weave is woven in, then what
         * the product offers in other weaves. Both stay clickable and equally
         * legible — picking from the second simply changes the fabric too, and
         * the caption between them says so. Hiding them, which is what this did
         * before, made a colour the product genuinely offers unreachable.
         */
        const offered = new Set(
            variations
                .filter((one) => !fabric || one.attributes?.attribute_pa_fabric === fabric)
                .map((one) => one.attributes?.attribute_pa_color)
        );

        /*
         * The fabric row is dimmed the same way, and for the same reason: a
         * weave that is not woven in the chosen shade is still a choice, it just
         * changes the colour as well. Symmetry here is what makes the two rows
         * read as one decision rather than two.
         */
        for (const tile of form.querySelectorAll('[data-swatch="pa_fabric"]')) {
            const wrap = tile.closest('.product-hero__swatch');

            if (!wrap) {
                continue;
            }

            const carries = variations.some(
                (one) =>
                    one.attributes?.attribute_pa_fabric === tile.value &&
                    one.attributes?.attribute_pa_color === colour
            );

            wrap.toggleAttribute('data-other', !carries);
            wrap.title = carries ? '' : `${plainName(wrap)} — ${chosenName(colour)}`;
        }

        /*
         * Every colour keeps its place. The row used to sort itself into
         * "in this weave" and "everything else", which meant the tiles moved
         * under the pointer on every fabric change and the page jumped. Now the
         * grid is fixed — same colours, same order, same number of rows — and
         * only the marking changes: a dot in the corner says this shade comes in
         * another cloth, and clicking it still switches the fabric.
         */
        for (const tile of form.querySelectorAll('[data-swatch="pa_color"]')) {
            const wrap = tile.closest('.product-hero__swatch');

            if (!wrap) {
                continue;
            }

            const here = offered.has(tile.value);

            wrap.hidden = false;
            tile.disabled = false;
            wrap.toggleAttribute('data-other', !here);
            wrap.title = here ? '' : `${plainName(wrap)} — ${wrap.dataset.in ?? ''}`;
        }

        clearExtra();
    }

    /*
     * A colour rested on, shown at once on the photograph beside it.
     *
     * Pointing is not choosing: the gallery lays the picture over the set and
     * takes it away again when the pointer leaves the row, and nothing about
     * the chosen colourway — the price, the form, the rail — moves. Bound to
     * the row rather than to each tile, so travelling from one to the next does
     * not flash the chosen photograph back in between.
     *
     * A colour this weave is not woven in still previews: clicking it would
     * switch the fabric too, and showing what that lands on is exactly the
     * question somebody hovering it is asking.
     */
    function setupPeek() {
        if (!gallery) {
            return;
        }

        const row = form
            .querySelector('[data-swatch="pa_color"]')
            ?.closest('.product-hero__swatches');

        if (!row || !window.matchMedia('(hover: hover)').matches) {
            return;
        }

        /**
         * Ask the gallery to lay one address over the stage, or to take it off.
         *
         * @param {string} src The photograph, or an empty string to clear it.
         * @returns {void}
         */
        const peek = (src) => {
            gallery.dispatchEvent(new CustomEvent('product-gallery:peek', { detail: { src } }));
        };

        const slot = form.querySelector('.product-hero__choice--color [data-extra]');

        /**
         * Put the weave a colour comes in on the Colour line, or take it off.
         *
         * @param {string} name The fabric's name, or an empty string to clear.
         * @returns {void}
         */
        const note = form.querySelector('[data-choice-note]');

        const say = (name) => {
            if (!slot) {
                return;
            }

            slot.textContent = name ? (slot.dataset.template ?? '%s').replace('%s', name) : '';
            slot.hidden = '' === name;
        };

        /*
         * What the last click did is about a colour already chosen; the badge
         * is about one merely pointed at. Side by side they read as a
         * contradiction, so the sentence goes the moment a pointer arrives in
         * the row — and stays gone until the next click writes a new one.
         *
         * Taken away once, on entering the row, rather than per tile: the line
         * occupies its own row of the column, so hiding and showing it moves
         * everything below. Tied to each tile it would do that on every step
         * from a colour this weave carries to one it does not, and the section
         * jumped under the pointer.
         */
        if (note) {
            row.addEventListener('mouseenter', () => {
                note.hidden = true;
            });
        }

        for (const tile of row.querySelectorAll('[data-swatch="pa_color"]')) {
            const wrap = tile.closest('.product-hero__swatch');

            if (!wrap) {
                continue;
            }

            wrap.addEventListener('mouseenter', () => {
                /*
                 * This colour, on everything else as it currently stands.
                 *
                 * Every other axis counts, not the weave alone: a bed offers
                 * three sizes, and previewing a colour on whichever size the
                 * table happened to list first would answer a question nobody
                 * asked. Candidates are scored on how much of the current
                 * choice they keep, so the closest wins — and a colour this
                 * weave does not carry still previews, in the weave that does,
                 * which is where clicking it would land.
                 */
                const held = Object.entries(current?.attributes ?? {}).filter(
                    ([key]) => 'attribute_pa_color' !== key
                );

                let found = null;
                let best = -1;

                for (const one of variations) {
                    if (one.attributes?.attribute_pa_color !== tile.value) {
                        continue;
                    }

                    const score = held.filter(
                        ([key, value]) => one.attributes?.[key] === value
                    ).length;

                    if (score > best) {
                        best = score;
                        found = one;
                    }
                }

                /* The cut-out, not the first of the gallery: that is the room
                 * scene, and the supplier ships one scene for every colourway
                 * of a product — the same file, byte for byte. Previewing it
                 * would show the same photograph whichever tile was under the
                 * pointer. */
                peek(found?.packshot?.full || found?.gallery?.[0]?.full || '');

                /*
                 * And the Colour line says what pressing it would mean. A shade
                 * this weave does not carry is still a choice, but taking it
                 * changes the weave as well — which the row only said after the
                 * fact, once the swap had happened and the visitor was looking
                 * at a colour they had not asked for. Said before the click, it
                 * is information rather than an apology.
                 */
                const weave = found?.attributes?.attribute_pa_fabric ?? '';

                say(
                    weave === (current?.attributes?.attribute_pa_fabric ?? '')
                        ? ''
                        : plainName(
                              form
                                  .querySelector(
                                      `[data-swatch="pa_fabric"][value="${CSS.escape(weave)}"]`
                                  )
                                  ?.closest('.product-hero__swatch')
                          )
                );
            });
        }

        row.addEventListener('mouseleave', () => {
            peek('');
            say('');
        });
    }

    /**
     * What one size costs against the size now chosen.
     *
     * Only sizes carry this. Checked against the catalogue rather than assumed:
     * of sixty variable products, not one prices a colour differently from
     * another at the same size, and fifteen price the sizes apart. A figure on
     * a colour would therefore be permanently empty.
     *
     * Measured from the size now chosen, not from the cheapest — standing on
     * the largest bed, "+ €240" beside the size you already have would be a lie
     * — and compared like for like: the same colour and weave where that
     * pairing exists, so the number is the cost of the size and of nothing
     * else.
     *
     * @param {string} size The size slug being considered.
     * @returns {string} The figure, or an empty string when there is none.
     */
    function extraFor(size) {
        const here = Number(current?.amount ?? 0);

        if (!money || !here) {
            return '';
        }

        const chosen = Object.fromEntries(
            Object.entries(current?.attributes ?? {}).map(([key, value]) => [
                key.replace(/^attribute_/, ''),
                value,
            ])
        );

        const sameSize = variations.filter(
            (one) => one.attributes?.attribute_pa_size === size && one.amount
        );

        /* Like for like where the pairing exists; the cheapest in that size
         * otherwise, which is what a shop quotes when the exact combination is
         * not made. */
        const alike = sameSize.filter(
            (one) =>
                (!chosen.pa_color || one.attributes?.attribute_pa_color === chosen.pa_color) &&
                (!chosen.pa_fabric || one.attributes?.attribute_pa_fabric === chosen.pa_fabric)
        );

        const pool = alike.length ? alike : sameSize;

        if (!pool.length) {
            return '';
        }

        const gap = Math.min(...pool.map((one) => Number(one.amount))) - here;

        return Math.abs(gap) < 0.005 ? '' : `${gap > 0 ? '+' : '−'} ${writeMoney(gap, money)}`;
    }

    /**
     * Say on the Fabric line that the weave moved, and which colour moved it.
     *
     * The sentence explaining it is announced to a screen reader and shown to
     * nobody: in the flow it pushed everything under it down as it arrived, and
     * held open permanently it put that space under the label for good. This
     * says the same thing where the change is already visible — beside the
     * fabric's own name, on a line that was there anyway.
     *
     * @param {string} colour The colour that was chosen, or '' to take it off.
     * @returns {void}
     */
    function tagFabric(colour) {
        const slot = form.querySelector('.product-hero__choice--fabric [data-extra]');

        if (!slot) {
            return;
        }

        slot.textContent = colour
            ? (slot.dataset.template ?? 'changed for %s').replace('%s', colour)
            : '';
        slot.hidden = '' === colour;
    }

    /**
     * Take the figure off the Size line.
     *
     * @returns {void}
     */
    function clearExtra() {
        const slot = form.querySelector('.product-hero__choice--size [data-extra]');

        if (slot) {
            slot.textContent = '';
            slot.hidden = true;
        }
    }

    /*
     * The Size line answers what the size under the pointer would cost.
     *
     * On the line rather than on the tiles: a figure printed under every size is
     * read as part of that size's name, and the question is only ever about the
     * one being considered. It clears when the pointer leaves the row, and again
     * whenever the choice changes, so the line never keeps an answer to a
     * question nobody is still asking.
     *
     * @returns {void}
     */
    function setupExtras() {
        const row = form
            .querySelector('.product-hero__choice--size [data-swatch="pa_size"]')
            ?.closest('.product-hero__swatches');
        const slot = form.querySelector('.product-hero__choice--size [data-extra]');

        if (!row || !slot || !window.matchMedia('(hover: hover)').matches) {
            return;
        }

        for (const tile of row.querySelectorAll('[data-swatch="pa_size"]')) {
            const wrap = tile.closest('.product-hero__swatch');

            if (!wrap) {
                continue;
            }

            const answer = () => {
                const figure = extraFor(tile.value);

                slot.textContent = figure;
                slot.hidden = '' === figure;
            };

            wrap.addEventListener('mouseenter', answer);

            // Keyboard reaches the row the same way the pointer does.
            tile.addEventListener('focus', answer);
        }

        row.addEventListener('mouseleave', clearExtra);
        row.addEventListener('focusout', (event) => {
            if (!row.contains(event.relatedTarget)) {
                clearExtra();
            }
        });
    }

    /**
     * Match the buy button to whether this colourway can actually be bought.
     *
     * Disabled *and* relabelled: a control that still posts would take the
     * click and fail, and one that only goes grey reads as broken rather than
     * as sold out. The wording comes from the markup, already translated.
     *
     * @param {boolean} inStock Whether the chosen variation is available.
     * @returns {void}
     */
    const showStock = (inStock) => {
        const button = form.querySelector('[data-submit]');

        if (!button) {
            return;
        }

        button.disabled = !inStock;
        button.textContent = inStock
            ? (button.dataset.label ?? button.textContent)
            : (button.dataset.soldOut ?? button.textContent);
    };

    /**
     * Point the fabric tile at the weave of the colourway now chosen.
     *
     * Bouclé in terracotta is not bouclé in beige, and the feed photographs each
     * one — so the tile is a picture of what is actually being ordered rather
     * than a stand-in for the material in general.
     *
     * @param {Object} photo The close-up, as `{ thumb, full }`.
     * @returns {void}
     */
    const showFabric = (photo) => {
        const image = root.querySelector('.product-hero__choice--fabric .product-hero__chip-image');

        if (!image || !photo?.thumb) {
            return;
        }

        image.src = photo.thumb;

        /* The srcset described the photograph being replaced; leaving it would
         * let the browser pick a size of the wrong picture. */
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
    };

    /*
     * A change on any axis is answered by finding the variation that matches
     * EVERY axis as it currently stands — matching on the changed one alone
     * would pick the first colourway the moment a second axis gains a value.
     */
    const chosen = () => {
        const picked = {};

        for (const input of form.querySelectorAll('[data-swatch]:checked')) {
            picked[input.dataset.swatch] = input.value;
        }

        return picked;
    };

    /**
     * What one variation answers on a given axis.
     *
     * @param {Object} one      A variation from the printed table.
     * @param {string} taxonomy The axis, without the `attribute_` prefix.
     * @returns {string|undefined} Its value on that axis.
     */
    const valueOn = (one, taxonomy) =>
        Object.entries(one.attributes ?? {}).find(
            ([key]) => key.replace(/^attribute_/, '') === taxonomy
        )?.[1];

    /**
     * The variation to show once somebody has picked `value` on `taxonomy`.
     *
     * Not every combination is offered: this catalogue sells a corner sofa in
     * eleven colours, but only eight of them in each of its two fabrics. An
     * exact match on all four axes therefore finds nothing quite often, and a
     * chooser that answers a click by doing nothing reads as broken.
     *
     * So the axis just clicked is honoured absolutely, and the rest are treated
     * as preferences — the candidate agreeing with most of them wins. Picking a
     * fabric the current colour does not come in moves the colour rather than
     * refusing the fabric, which is the choice the reader actually expressed.
     *
     * @param {string} taxonomy The axis that changed.
     * @param {string} value    What was picked on it.
     * @returns {Object|undefined} The best available variation.
     */
    const bestFor = (taxonomy, value) => {
        const picked = chosen();
        const others = Object.entries(picked).filter(([key]) => key !== taxonomy);

        let best;
        let bestScore = -1;

        for (const one of variations) {
            if (valueOn(one, taxonomy) !== value) {
                continue;
            }

            const score = others.filter(([key, want]) => valueOn(one, key) === want).length;

            if (score > bestScore) {
                best = one;
                bestScore = score;
            }

            // Nothing can beat agreeing with every other axis.
            if (bestScore === others.length) {
                break;
            }
        }

        return best;
    };

    /* Draw once for the variation the server opened on: the colour row has to
     * be narrowed to the chosen fabric before the first click, not after it. */
    const opening = form.querySelector('[data-variation-id]')?.value ?? '';
    const first =
        variations.find((one) => String(one.id) === String(opening)) ??
        bestFor('pa_fabric', form.querySelector('[data-swatch="pa_fabric"]:checked')?.value ?? '');

    if (first) {
        /* The colourway the page opened on, so the first click on a colour is
         * read as a choice rather than as the page still arriving. */
        current = first;
        render(first);
    }

    setupPeek();
    setupExtras();

    for (const input of form.querySelectorAll('[data-swatch]')) {
        input.addEventListener('change', () => {
            /* What was chosen before this click, so a colour that had to move
             * can be named rather than silently swapped underneath. */
            const was = form.querySelector('[data-swatch="pa_color"]:checked')?.value ?? '';
            const wasFabric = form.querySelector('[data-swatch="pa_fabric"]:checked')?.value ?? '';
            const next = bestFor(input.dataset.swatch, input.value);

            show(next);

            const note = form.querySelector('[data-choice-note]');

            if (!note) {
                return;
            }

            const now = next?.attributes?.attribute_pa_color ?? was;

            /*
             * The mirror case. Picking a colour the chosen weave does not carry
             * is a legitimate choice — it changes the fabric — and saying so is
             * what stops the fabric row appearing to move on its own.
             */
            if ('pa_color' === input.dataset.swatch) {
                const woven = next?.attributes?.attribute_pa_fabric ?? '';
                const changed = woven && woven !== wasFabric;

                note.hidden = !changed;
                tagFabric(
                    changed ? plainName(input.closest('.product-hero__swatch')) || input.value : ''
                );

                if (changed) {
                    const fabricTile = form
                        .querySelector(`[data-swatch="pa_fabric"][value="${CSS.escape(woven)}"]`)
                        ?.closest('.product-hero__swatch');

                    note.textContent = (
                        note.dataset.templateFabric ??
                        'Fabric changed to %2$s — the one %1$s is woven in.'
                    )
                        .replace(
                            '%1$s',
                            plainName(input.closest('.product-hero__swatch')) || input.value
                        )
                        .replace('%2$s', plainName(fabricTile) || woven);
                }

                return;
            }

            const moved = input.dataset.swatch === 'pa_fabric' && was && now && was !== now;

            note.hidden = !moved;

            // Chosen outright, so no longer a consequence of anything.
            tagFabric('');

            if (moved) {
                const name = (slug) =>
                    plainName(
                        form
                            .querySelector(`[data-swatch="pa_color"][value="${CSS.escape(slug)}"]`)
                            ?.closest('.product-hero__swatch')
                    ) || slug;

                const fabric = plainName(input.closest('.product-hero__swatch')) || input.value;

                note.textContent = (
                    note.dataset.template ?? '%2$s is not woven in %1$s, so %3$s is shown instead.'
                )
                    .replace('%1$s', name(was))
                    .replace('%2$s', fabric)
                    .replace('%3$s', name(now));
            }
        });
    }
}

for (const root of document.querySelectorAll('.product-hero')) {
    enhance(root);
}

/*
 * Why the shop refused, said where the button is.
 *
 * The cart drawer does the asking and hears the answer; it has no idea where on
 * a product page a reason belongs, so it announces one and this block writes
 * it. Cancelling the event is how this block says it has — otherwise the drawer
 * posts the form for real and the reason is shown on a fresh page, which works
 * and costs the reader their place.
 */
document.addEventListener('cart:refused', (event) => {
    const form = event.detail?.form;
    const slot = form?.querySelector('[data-refused]');

    if (!slot) {
        return;
    }

    const messages = (event.detail?.messages ?? []).filter(Boolean);

    if (!messages.length) {
        return;
    }

    event.preventDefault();

    slot.textContent = messages.join(' ');
    slot.hidden = false;

    slot.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
});

/* A new choice is a new attempt: whatever was wrong a moment ago may not be. */
document.addEventListener('change', (event) => {
    if (!event.target.closest('.product-hero__form')) {
        return;
    }

    const slot = event.target.closest('.product-hero__form')?.querySelector('[data-refused]');

    if (slot) {
        slot.hidden = true;
    }
});
